// src/components/ContactForm/ContactForm.tsx
import React, { useState, useEffect } from 'react';
import { Send, AlertCircle, Loader2, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import useContactForm from '../../../hooks/useContactForm';
import Notification from '../../../components/misc/Notification';

export interface ContactFormProps {
  className?: string;
  title?: string;
  subtitle?: string;
  isDark?: boolean;
  hideFAQLink?: boolean;
}

export default function ContactForm({
  className = '',
  title,
  subtitle,
  isDark = false,
  hideFAQLink = false,
}: ContactFormProps) {
  const { t } = useTranslation();

  // Estados para el formulario
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const { loading, error, success, sendContactForm } = useContactForm();
  const [errorMessage, setErrorMessage] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);

  // Estado para el checkbox de política de privacidad
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);

  // Al haber éxito, limpiamos el formulario y mostramos notificación
  useEffect(() => {
    if (success) {
      setFormData({ name: '', email: '', company: '', message: '' });
      showNotificationMessage(t('contact_form.success') || 'Solicitud enviada correctamente.');
    }
  }, [success, t]);

  // Función para mostrar notificación
  const showNotificationMessage = (message: string) => {
    setNotificationMessage(message);
    setShowNotification(true);
  };

  // Enviar formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validar que se acepte la política de privacidad
    if (!acceptedPolicy) {
      setErrorMessage(t('contact_form.errors.policyNotAccepted'));
      return;
    }

    try {
      await sendContactForm({
        name: formData.name,
        email: formData.email,
        company_name: formData.company,
        message: formData.message,
      });
    } catch (err) {
      setErrorMessage(t('contact_form.error_message'));
    }
  };

  return (
    <div
      id="contact-section"
      className={`max-w-6xl mx-auto p-14 bg-[#101827] rounded-xl shadow-xl border border-gray-700 
        ${hideFAQLink ? 'flex flex-col' : 'flex flex-col lg:flex-row'} gap-12 ${className}`}
    >
      {/* Sección Izquierda (FAQs) solo si hideFAQLink === false */}
      {!hideFAQLink && (
        <div className="lg:w-1/3 flex flex-col justify-center text-left">
          <h3 className="text-3xl font-bold text-white mb-3">
            {t('contact_form.doubts')}
          </h3>
          <p className="text-lg text-gray-400 mb-4">
            {t('contact_form.faqs')}
          </p>
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-all text-lg font-semibold"
          >
            <HelpCircle className="h-5 w-5" />
            {t('contact_form.goto_faqs')}
          </Link>
        </div>
      )}

      {/* Sección Derecha (Formulario) */}
      <div className={`${hideFAQLink ? 'w-full' : 'lg:w-2/3'}`}>
        {/* Título y subtítulo opcionales */}
        {title && (
          <h2 className="text-3xl font-extrabold text-white text-center mb-2">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-lg text-center text-gray-400 mb-6">
            {subtitle}
          </p>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nombre */}
            <div>
              <label htmlFor="name" className="block text-lg font-medium text-gray-300 mb-2">
                {t('contact_form.name')}
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                           text-white text-lg transition-all"
                required
                disabled={loading}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-lg font-medium text-gray-300 mb-2">
                {t('contact_form.email')}
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                           text-white text-lg transition-all"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Empresa */}
          <div>
            <label htmlFor="company" className="block text-lg font-medium text-gray-300 mb-2">
              {t('contact_form.company')}
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         text-white text-lg transition-all"
              disabled={loading}
            />
          </div>

          {/* Mensaje */}
          <div>
            <label htmlFor="message" className="block text-lg font-medium text-gray-300 mb-2">
              {t('contact_form.message')}
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         text-white text-lg transition-all"
              required
              disabled={loading}
            />
          </div>

          {/* Checkbox de política de privacidad */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="privacyPolicy"
              checked={acceptedPolicy}
              onChange={(e) => setAcceptedPolicy(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="privacyPolicy" className="ml-2 text-lg text-gray-300">
              {t('contact_form.acceptPolicy')}{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                {t('contact_form.privacyPolicy')}
              </a>
            </label>
          </div>

          {/* Errores */}
          {(errorMessage || error) && (
            <div className="text-red-500 text-lg flex items-center gap-2">
              <AlertCircle className="h-6 w-6" />
              {error || errorMessage}
            </div>
          )}

          {/* Botón Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-4 
                       rounded-lg text-lg font-semibold flex items-center justify-center gap-2 
                       group transition-all shadow-lg hover:scale-105 hover:shadow-xl 
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                {t('contact_form.processing')}
                <Loader2 className="h-6 w-6 animate-spin" />
              </>
            ) : (
              <>
                {t('contact_form.submit')}
                <Send className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>

          {/* Mensaje de éxito */}
          {success && (
            <p className="text-lg text-center text-green-400 mt-4">
              {t('contact_form.success')}
            </p>
          )}
        </form>
      </div>

      {/* Notificación */}
      {showNotification && (
        <Notification
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}
    </div>
  );
}
